import React, { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
    DeleteFilled,
    EditFilled,
    FileFilled,
    HomeOutlined
} from "@ant-design/icons";
import { Button, Input, Pagination, Space, Table, Tag } from "antd";

import { useGetVitalsReportQuery } from "../../utils/apiSlice";

import { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import './MyVitalsReport.scss';

const AppointmentList = () => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}");
    const patientID = userData?.patientId;


    const [activeVital, setActiveVital] = useState("BP");
    useEffect(() => {
        setCurrentPage(1);
    }, [activeVital]);
    const { data: apiResponse, isLoading, isError } = useGetVitalsReportQuery(
        {
            patientId: patientID,
            vitalType: activeVital // BP | HR | GLUCOSE | SPO2
        },
        {
            skip: !patientID || !activeVital
        }
    );

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    const { dataSource, chartCategories, chartSeries } = useMemo(() => {
        if (!apiResponse?.data) return { dataSource: [], chartCategories: [], chartSeries: [] };

        const formattedTable = apiResponse.data.map((item: any) => ({
            ...item,
            key: item.id,

            measuredAt: new Date(item.measuredAt).toLocaleString('en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
            }),

            test: item.vitalType === "BP" ? `${item.valueText}` : `${item.valueNumeric} ${item.unit || ''}`,
            status: item.qualityFlag || "Confirmed",
            source: "Device"
        }));

        const categories = apiResponse.data.map((item: any) =>
            new Date(item.measuredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        );

        const seriesData = apiResponse.data.map((item: any) => item.valueNumeric);

        return {
            dataSource: formattedTable,
            chartCategories: categories,
            chartSeries: seriesData
        };
    }, [apiResponse]);

    const filteredDataSource = useMemo(() => {
        return dataSource.filter((item: any) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchText.toLowerCase())
            )
        );
    }, [dataSource, searchText]);
    const chartOptions: ApexOptions = {
        chart: {
            height: 350,
            type: "line",
            toolbar: { show: false },
        },
        stroke: { width: 3, curve: "smooth" },
        xaxis: {
            categories: chartCategories,
        },
        title: {
            text: `${dataSource[0]?.vitalType || 'Vital'} Trends`,
            style: { fontWeight: 'bold' }
        },
        tooltip: { theme: "dark" },
    };

    const chartData = [
        {
            name: dataSource[0]?.vitalType || "Value",
            data: chartSeries,
        },
    ];




    const columns = useMemo(() => {
        let valueColumnTitle = "";


        switch (activeVital) {
            case "BP":
                valueColumnTitle = "Systolic/Diastolic";
                break;
            case "HR":
                valueColumnTitle = "BPM";
                break;
            case "GLUCOSE":
                valueColumnTitle = "Glucose mg/dL";
                break;
            case "SPO2":
                valueColumnTitle = "SpO2 (%)";
                break;
            default:
                valueColumnTitle = "Value";
        }

        return [
            {
                title: "Vital Type",
                dataIndex: "vitalType",
                key: "vitalType",
            },
            {
                title: "Date & Time",
                dataIndex: "measuredAt",
                key: "measuredAt",
            },
            {
                title: valueColumnTitle,
                dataIndex: "test",
                key: "test",
            },
            {
                title: "Source",
                dataIndex: "source",
                key: "source",
                render: (source: any) => (
                    <Tag color={source === "Device" ? "blue" : "default"}>
                        {source}
                    </Tag>
                ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (status: any) => (
                    <Tag color={status === "Confirmed" ? "green" : "orange"}>
                        {status}
                    </Tag>
                ),
            },
            {
                title: "Notes",
                dataIndex: "notes",
                key: "notes",
            },
            {
                title: "Action",
                key: "action",
                render: () => (
                    <Space>
                        <Button shape="circle" icon={<EditFilled />} className="btn-edit" />
                        <Button shape="circle" icon={<DeleteFilled />} className="btn-delete" />

                    </Space>
                ),
            },
        ];
    }, [activeVital]);

    // if (isLoading) return <div className="p-5">Loading Vitals...</div>;
    // if (isError) return <div className="p-5 text-danger">Error fetching reports.</div>;


    return (
        <div className="content-wrapper">
            <div className="container-full table-padding">

                <div className="page-header">


                    <div className="breadcrumb">
                        <h3 className="page-title">My Vital Reports</h3>
                        <span className="customize-breadcrumb">
                            <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                            <span className="separator"> - </span>
                            <span className="current">My Vital Reports</span>
                        </span>

                    </div>
                </div>
                <div className="box padding-box">

                    <div className="card-header-vitals p-3">
                        <h2 className="card-inner-title  inner-title mb-3">My Vital Reports</h2>
                        <div className="d-flex gap-2 mb-4">
                            <Button
                                className={`btn-tab tab-color ${activeVital === 'BP' ? 'active' : ''}`}
                                onClick={() => setActiveVital('BP')}
                            >
                                BP
                            </Button>

                            <Button
                                className={`btn-tab tab-color ${activeVital === 'HR' ? 'active' : ''}`}
                                onClick={() => setActiveVital('HR')}
                            >
                                Heart Rate
                            </Button>

                            <Button
                                className={`btn-tab tab-color ${activeVital === 'GLUCOSE' ? 'active' : ''}`}
                                onClick={() => setActiveVital('GLUCOSE')}
                            >
                                Glucose
                            </Button>

                            <Button
                                className={`btn-tab tab-color ${activeVital === 'SPO2' ? 'active' : ''}`}
                                onClick={() => setActiveVital('SPO2')}
                            >
                                Oxygen
                            </Button>
                        </div>
                        <div className="box padding-box">
                            <div className="d-flex justify-content-between align-items-center mb-3">

                                <div className="chart-container" style={{ width: "100%", padding: "0 20px" }}>
                                    <ReactApexChart
                                        options={chartOptions}
                                        series={chartData}
                                        type="line"
                                        height={350}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Table Section */}
                    <div className="table-responsive">
                        <div style={{ padding: "10px 20px" }}>
                            <Input.Search placeholder="Search vitals..." className="vitals-search-input" style={{ width: 250 }} value={searchText} onChange={(e) => setSearchText(e.target.value)} allowClear />
                        </div>
                        <Table
                            columns={columns}
                            dataSource={filteredDataSource}
                            loading={isLoading}
                            locale={{
                                emptyText: isError
                                    ? "Error fetching data"
                                    : "No Vitals Found",
                            }}
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: filteredDataSource.length,
                                showSizeChanger: true,
                                pageSizeOptions: ["5", "10", "20", "50"],
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                },
                            }}
                        />
                        {/* <div className="family-footer">
                            <span>Total {filteredDataSource.length} Vitals</span>
                            <Pagination
                                current={currentPage}
                                pageSize={pageSize}
                                total={filteredDataSource.length}
                                showSizeChanger
                                pageSizeOptions={["5", "10", "20", "50"]}
                                onChange={(page, size) => {
                                    setCurrentPage(page);
                                    setPageSize(size);
                                }}
                                onShowSizeChange={(_, size) => {
                                    setCurrentPage(1);
                                    setPageSize(size);
                                }}
                            />
                        </div> */}
                    </div>



                </div>
            </div>














        </div>


    );
};

export default AppointmentList;